/**
 * Pricing-guidance backend error codes.
 *
 * Must match `ErrorCodes` in be/utils/errors.js (Rate Guidance group).
 *
 * Codes are the stable API contract; the backend's `error` string is Arabic and
 * exists for legacy clients and logs. Translation lives in the locale files
 * (`pricing.errors.*`) — these are just the identifiers.
 *
 * Client rule: if a response carries `code`, translate it; otherwise fall back to a
 * generic message. Never infer meaning from message text.
 *
 * ⚠️ These codes only survive the wire when the request sets
 * `preserveBackendError: true`. `secureApi`'s interceptor REPLACES
 * `error.response.data` wholesale on 403/404/500, which destroys the code itself —
 * so `RATE_GUIDANCE_UNIT_NOT_FOUND` (404) and `SYSTEM_ERROR` (500) degrade to a
 * generic "unexpected error" without the flag. Documented at notificationApi.js:16
 * after that exact bug was found by audit.
 *
 * Pattern reference: web/src/utils/arrivalErrorCodes.js
 */
export const PRICING_ERROR_CODES = {
  UNIT_NOT_FOUND: 'RATEGUIDE_001',
  INVALID_DATES: 'RATEGUIDE_002',
  INVALID_POLICY: 'RATEGUIDE_003',
  INVALID_DISCOUNT: 'RATEGUIDE_004',
  // Not a message: the Airbnb discounts card is switched off, so the page hides it.
  OFFERS_DISABLED: 'RATEGUIDE_005',
  INVALID_ID: 'VAL_003'
};

/** Locale key per code. Anything unmapped falls back to the caller's generic key. */
const CODE_TO_KEY = {
  [PRICING_ERROR_CODES.UNIT_NOT_FOUND]: 'pricing.errors.unitNotFound',
  [PRICING_ERROR_CODES.INVALID_DATES]: 'pricing.errors.invalidDates',
  [PRICING_ERROR_CODES.INVALID_POLICY]: 'pricing.errors.invalidPolicy',
  [PRICING_ERROR_CODES.INVALID_DISCOUNT]: 'pricing.errors.invalidDiscount',
  [PRICING_ERROR_CODES.INVALID_ID]: 'pricing.errors.invalidUnit',
  NOT_FOUND: 'pricing.errors.unitNotFound',
  ACCESS_DENIED: 'pricing.errors.forbidden',
  SERVER_ERROR: 'pricing.errors.system',
  SYSTEM_ERROR: 'pricing.errors.system'
};

/**
 * Translate a failed request into a message the operator can act on.
 *
 * The backend's Arabic `error` string is deliberately NOT used: it is one language on
 * a surface that ships two, and the code carries the same meaning without that
 * problem. `fallbackKey` covers a network failure or an endpoint predating the
 * convention — cases with no code at all.
 */
export const pricingErrorMessage = (error, t, fallbackKey) => {
  const code = error?.response?.data?.code;
  const key = code && CODE_TO_KEY[code];
  return t(key || fallbackKey);
};

export default PRICING_ERROR_CODES;
